/* ============================================================
   assets/int-zoom.js                        INTENTION — 2026-09-03
   TAP / CLICK TO ZOOM. One implementation, every PDP, every width.

   ⚑ WHERE IT CAME FROM. This was block 7 of assets/int-pdp-mobile.js,
   lifted out verbatim on 2026-09-03. That file bails out entirely above
   820px (`if (!matchMedia(MQ).matches) return;`), so zoom existed on the
   apparel phone layout and nowhere else: not on apparel desktop, not on
   framed print at any width. Nils asked for it everywhere, so it had to
   stop living inside a file that is mobile-only by construction.

   ⚑ WHY ZOOM AT ALL, AND WHY OURS. The theme ships PhotoSwipe and it opens
   the WRONG PHOTO: PhotoSwipe indexes the full media list while
   int-variant-gallery.liquid filters the visible gallery down to the
   selected colour, so on a lilak tee it opened a Deep Black shot. It also
   loads the 3000x3000 master with no width parameter. custom-1.css hides
   `[data-zoom-button]` and the theme's own zoom stays off on purpose.
   This reads the SLIDE THAT IS ACTUALLY ON SCREEN and reloads that same
   file at 1600px, so there is no index to get wrong and no 3000px master
   on mobile data.

   The hero is cut to ~60% of the viewport on the phone, and shrinking it
   is only defensible WITH zoom (Baymard: 25% of sites fail on resolution
   or zoom, and shoppers abandon over it). It is the other half of that
   change, not a nice-to-have.

   ⚑ NO WIDTH GATE. Deliberate. The overlay is full screen at every size and
   the pill is placed by measurement, not by breakpoint.

   ⚑ ONE GUARD, TWO CALLERS. sections/product.liquid and
   sections/product-framedprint.liquid both load this file. The guard below
   means loading it twice is harmless.

   ⚑ ITS CSS IS assets/int-zoom.css, loaded by the same two sections. Do not
   put zoom rules back into int-pdp-mobile.css — that sheet is not loaded on
   the framed print template.

   ⚑ 2026-09-07 — RESOLUTION AND THE OPENING FRAME. Two things were measured
   on the draft at 1440x900 dpr 2 and fixed here. The overlay was opening on
   an EMPTY img (0x0, complete=false) while the big file downloaded, and the
   big file was a constant 1600 while `.is-zoomed` paints the picture across
   3600 CSS px, which is 7200 device px. See the two blocks below.

   ⛔ THE AT-REST GALLERY IMAGE WAS NOT PART OF THIS. FINDINGS-pdp-zoom-
   resolution-2026-09-06.md reports the slide holding a 499px file in a 464px
   box and calls it half resolution. It is not. `naturalWidth` on an img that
   picked its file from a `w`-descriptor srcset is divided by the current
   pixel density: 1024w chosen for a 500px `sizes` gives ~2.05, and
   1024 / 2.05 = 499. Proved twice on the draft 2026-09-07 — currentSrc is
   `width=1024` and the network transferred 35,226 bytes on the fitted tee
   and 45,086 on the sweatshirt, byte-matching the CDN's real 1024x1024. The
   box needs 928 device px and has 1024. **`sizes` in snippets/media.liquid
   is correct. Do not change it, and do not trust naturalWidth on a
   srcset img again.**
   ============================================================ */
(function () {
  'use strict';
  if (window.__intZoom) return;

  function q(sel, root) { return (root || document).querySelector(sel); }

  var mounted = false;
  function mount() {
    if (mounted) return;
    var grid = q('.product__grid');
    if (!grid) return;
    mounted = true;

    function currentImg() {
      return grid.querySelector('[data-media-slide].is-selected img')
          || grid.querySelector('.flickity-cell.is-selected img')
          || grid.querySelector('[data-media-slide] img');
    }
    /* ══════════════════════════════════════════════════════════════════
       WHICH FILE THE OVERLAY LOADS. Measured on the draft 2026-09-07,
       apparel PDP, 1440x900 dpr 2:

         fit state    displayed  900 x  900 CSS  ->  1,800 device px
         .is-zoomed   displayed 3600 x 3600 CSS  ->  7,200 device px
         file it held 1600 wide                  ->  4.5x short

       1600 was a constant. It is now derived from what `.is-zoomed`
       actually paints: int-zoom.css sets `width: 250%` of the scroll
       container, so the need is 2.5 x viewport width x dpr.

       ⚑ TWO CEILINGS, BOTH DELIBERATE (Nils, 2026-09-07).
       · the stored master, read off the at-rest srcset's largest `w`
         descriptor. The CDN never upscales, so asking above the master
         returns the master anyway — but under a URL nothing else on the
         page will ever reuse.
       · 2000 below 821px. The zoomed need on a phone is ~2,900 device px
         and 3000 costs 164KB against 92KB at 2000. Nils budgeted ~126KB
         per zoom on 2026-09-06: desktop takes the master, phones do not.

       Cost of this picture, WebP, real image Accept header, measured:
         1600 66KB · 1920 87KB · 2000 92KB · 3000 164KB
       ══════════════════════════════════════════════════════════════════ */
    /* ⚑ 2026-09-07, SECOND CLICK. int-zoom.css sets `width: 250%` of the
       SCROLL CONTAINER, which is the viewport, not the picture. Measured on
       the draft, that is not one zoom level, it is two:

         desktop 1440x900   fit 900px -> 3600px   = 4.0x the fitted picture
         mobile  375        fit 375px ->  938px   = 2.5x

       The fit state is letterboxed by HEIGHT on a wide screen, so the wider
       the screen the harder it zoomed. The zoomed size is now set from JS as
       a multiple of the FITTED box, which is a level we can actually choose.

       ⚑ TWO FACTORS ON PURPOSE, Nils 2026-09-07. Desktop was too deep at 4x.
       Mobile at 2.5x was right, and a phone fits the picture by WIDTH, so
       2.5x of the fitted box IS the 2.5x of the viewport it has always had:
       the phone is unchanged. A desktop already shows the picture large in
       the fit state, so it needs less. ⚑ These two numbers ARE the zoom
       level, change them here and nowhere else. The inline width beats the
       `250%` in int-zoom.css, which stays only as a fallback. */
    var ZOOM_DESKTOP = 2;
    var ZOOM_MOBILE  = 2.5;
    function zoomFactor() { return window.innerWidth <= 820 ? ZOOM_MOBILE : ZOOM_DESKTOP; }
    var CAP_MOBILE  = 2000;
    var CAP_DESKTOP = 3000;
    var FLOOR       = 1600;  /* never worse than before this change */
    var LADDER      = [1600, 1920, 2000, 3000];

    function masterWidth(img) {
      var ss = img && img.getAttribute('srcset');
      var m = ss && ss.match(/(\d+)w/g);
      if (!m) return CAP_DESKTOP;
      var max = 0;
      m.forEach(function (s) { var n = parseInt(s, 10); if (n > max) max = n; });
      return max || CAP_DESKTOP;
    }

    /* The contain-fit of an aspect ratio inside the overlay, in CSS px. */
    function fitBox(ar) {
      var vw = (ov && ov.clientWidth)  || window.innerWidth;
      var vh = (ov && ov.clientHeight) || window.innerHeight;
      var w = vw, h = vw / ar;
      if (h > vh) { h = vh; w = vh * ar; }
      return { w: w, h: h };
    }

    function zoomWidth(img) {
      var cap  = Math.min(window.innerWidth <= 820 ? CAP_MOBILE : CAP_DESKTOP, masterWidth(img));
      var need = Math.round(zoomFactor() * fitBox(aspectOf(img)).w * (window.devicePixelRatio || 1));
      var want = Math.max(Math.min(need, cap), Math.min(FLOOR, cap));
      for (var i = 0; i < LADDER.length; i++) {
        if (LADDER[i] >= want) return Math.min(LADDER[i], cap);
      }
      return cap;
    }

    /* Shopify CDN sizing lives in the query string. Swap the width we were
       served for the one we want; never hand back the bare master. */
    function zoomSrc(src, w) {
      if (!src) return '';
      src = src.split(' ')[0];
      if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, '$1' + w);
      return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    }

    function widthOf(src) {
      var m = src && src.match(/[?&]width=(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    }

    /* ⚑ The RATIO survives the density division that makes naturalWidth
       read 499 (see the header). The width/height attributes are exact
       when the theme emitted them, so prefer those. */
    function aspectOf(img) {
      var w = parseFloat(img.getAttribute('width'));
      var h = parseFloat(img.getAttribute('height'));
      if (w > 0 && h > 0) return w / h;
      if (img.naturalWidth > 0 && img.naturalHeight > 0) return img.naturalWidth / img.naturalHeight;
      return 1;
    }

    var ov = null, ovImg = null, zoomed = false, reqToken = 0;

    function build() {
      ov = document.createElement('div');
      ov.className = 'int-zoom';
      ov.setAttribute('role', 'dialog');
      ov.setAttribute('aria-modal', 'true');
      ov.innerHTML =
        '<button type="button" class="int-zoom__close" aria-label="Close">&#215;</button>' +
        '<div class="int-zoom__scroll"><img class="int-zoom__img" alt=""></div>';
      document.body.appendChild(ov);
      ovImg = ov.querySelector('.int-zoom__img');
      ov.querySelector('.int-zoom__close').addEventListener('click', close, false);
      ovImg.addEventListener('click', function (ev) { toggle(ev); }, false);
      ov.querySelector('.int-zoom__scroll').addEventListener('click', function (ev) {
        if (ev.target === ev.currentTarget) close();
      }, false);
      document.addEventListener('keydown', function (ev) {
        if (ev.key === 'Escape' && ov && ov.classList.contains('is-open')) close();
      }, false);
    }

    /* ══════════════════════════════════════════════════════════════════
       WHY THE FIT STATE IS SIZED HERE AND NOT BY max-width/max-height.
       The overlay now opens on the picture the page has already decoded
       and swaps the big file in when it lands. With `width: auto;
       height: auto; max-width: 100%; max-height: 100%` the box follows
       the INTRINSIC size, so a 1024px at-rest file on a 1300px-tall
       screen paints 1024 wide and then GROWS to 1300 when the 3000
       arrives. On Nils' 2424px screen that is a real jump, not a
       theoretical one.

       Computing the contain-fit ourselves makes the box identical before
       and after the swap, and keeps the img box exactly the picture — so
       the letterbox around it still belongs to .int-zoom__scroll and
       still closes the overlay when it is clicked.
       ══════════════════════════════════════════════════════════════════ */
    function fitToViewport() {
      if (!ov || !ovImg || zoomed) return;
      var ar = ovImg.__intAR;
      if (!ar || !ov.clientWidth || !ov.clientHeight) return;
      var b = fitBox(ar);
      ovImg.__intFit = b;
      ovImg.style.width  = Math.round(b.w) + 'px';
      ovImg.style.height = Math.round(b.h) + 'px';
    }

    function toggle(ev) {
      if (!ov) return;
      zoomed = !zoomed;
      ov.classList.toggle('is-zoomed', zoomed);
      /* ⚑ The zoomed size is zoomFactor() x the fitted box, set inline so it
         beats the `width: 250%` in int-zoom.css. Same multiple on a phone
         and on a 2424px desktop. */
      if (zoomed) {
        var b = ovImg.__intFit || fitBox(ovImg.__intAR || 1), z = zoomFactor();
        ovImg.style.width  = Math.round(b.w * z) + 'px';
        ovImg.style.height = Math.round(b.h * z) + 'px';
      } else { fitToViewport(); }
      if (zoomed && ev) {
        /* keep the point that was tapped under the finger */
        var sc = ov.querySelector('.int-zoom__scroll');
        var rx = ev.clientX / window.innerWidth;
        var ry = ev.clientY / window.innerHeight;
        requestAnimationFrame(function () {
          sc.scrollLeft = (sc.scrollWidth - sc.clientWidth) * rx;
          sc.scrollTop = (sc.scrollHeight - sc.clientHeight) * ry;
        });
      }
    }

    function open() {
      /* INT 2026-10-03 gallery reel: a film slide has nothing to zoom, and a tap on it is
         for its sound button. Nils: "hide zoom on that slide". See snippets/int-gallery-reel.liquid. */
      if (grid.querySelector('[data-media-slide].is-selected[data-type="int-reel"]')) return;
      var im = currentImg();
      if (!im) return;
      if (!ov) build();
      zoomed = false;
      ov.classList.remove('is-zoomed');

      var atRest = im.currentSrc || im.src;
      var big    = zoomSrc(atRest, zoomWidth(im));
      var token  = ++reqToken;

      /* ⚑ NEVER OPEN ON AN EMPTY IMG. Measured on the draft 2026-09-07:
         assigning the big file straight to .int-zoom__img left it at 0x0
         with complete === false at the moment the overlay became
         visible, so the first thing a shopper saw was a blank white
         screen for the length of the download. Paint the file the page
         has already decoded, then swap. */
      ovImg.__intAR = aspectOf(im);
      ovImg.alt = im.getAttribute('alt') || '';
      if (ovImg.src !== big && ovImg.src !== atRest) ovImg.src = atRest;
      ov.classList.add('is-open');
      fitToViewport();
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');

      /* ⚑ NEVER DOWNGRADE. Measured on the draft 2026-09-07 at 375px: the
         page had already put `width=3000` on the hero, and the phone cap
         of 2000 would have spent another 106KB to make the picture WORSE
         than the one already decoded. Upgrade only when the target is
         genuinely bigger than what we are showing. */
      if (big && big !== atRest && zoomWidth(im) > widthOf(atRest)) upgrade(big, token);
    }

    /* Decode off-DOM first. Assigning a src that is already complete in
       cache leaves the previous frame on screen until the new one is
       ready, so there is nothing to flash through — and fitToViewport()
       has already put the box at its final size, so nothing moves. */
    function upgrade(url, token) {
      var pre = new Image();
      pre.onload = function () {
        if (token !== reqToken) return;                        /* a later open won */
        if (!ov || !ov.classList.contains('is-open')) return;   /* closed meanwhile */
        ovImg.src = url;
      };
      pre.src = url;
    }

    function close() {
      if (!ov) return;
      reqToken++;              /* strand any upgrade still in flight */
      zoomed = false;          /* it was never reset here, so the next
                                  toggle after closing while zoomed used
                                  to turn zoom OFF on a fit-state overlay */
      ov.classList.remove('is-open', 'is-zoomed');
      ovImg.style.width = '';
      ovImg.style.height = '';
      document.documentElement.style.removeProperty('overflow');
    }

    /* A swipe through the gallery must not open the overlay, so the tap is
       only a tap if the finger barely moved.
       ⚑ 2026-09-20: AND ONLY IF IT WAS BRIEF. The 10px budget alone cannot tell a
       tap from a hesitant swipe, because both stay inside it. Only the DURATION
       separates them. MEASURED on this draft before this change, dispatching real
       PointerEvents at .product__grid at 390x844:
           2px drift,  held   30ms  ->  overlay OPENED   (correct, a tap)
           8px sideways, held 919ms  ->  overlay OPENED   ⛔ meant to be a swipe
           6px down,   held 1150ms  ->  overlay OPENED   ⛔ meant to be a scroll
           9px drift,  held 1799ms  ->  overlay OPENED   ⛔ a hesitant press
         120px swipe,  held  200ms  ->  overlay closed   (correct)
         200px scroll, held  300ms  ->  overlay closed   (correct)
       A real tap is under 150ms, so 500ms clears all three bad cases with margin
       and still leaves a deliberate tap forgiving.
       ⛔ Do NOT shrink the 10px instead. That budget is what lets a real tap survive
       a thumb roll, and a slow 4px press would still open the overlay.
       ⚑ pointercancel counts as movement. If the browser takes the gesture over for
       a scroll, this was never a tap.
       ⚑ The .int-zoom-pill button below is a separate, explicit click path and is
       deliberately NOT gated: pressing the Zoom pill always opens the overlay.
       Record: INTENTION_Shared/APPLIED-gallery-swipe-axis-2026-09-20.md */
    var TAP_MS = 500;
    function _intNow() {
      return (window.performance && window.performance.now) ? window.performance.now() : Date.now();
    }
    var sx = 0, sy = 0, moved = false, sT = 0;
    grid.addEventListener('pointerdown', function (e) {
      sx = e.clientX; sy = e.clientY; moved = false; sT = _intNow();
    }, true);
    grid.addEventListener('pointermove', function (e) {
      if (Math.abs(e.clientX - sx) > 10 || Math.abs(e.clientY - sy) > 10) moved = true;
    }, true);
    grid.addEventListener('pointercancel', function () { moved = true; }, true);
    grid.addEventListener('pointerup', function (e) {
      if (moved) return;
      if (_intNow() - sT > TAP_MS) return;
      if (e.target && e.target.closest && e.target.closest('.flickity-prev-next-button')) return;
      if (e.target && e.target.closest && e.target.closest('[data-type="int-reel"]')) return;
      /* 2026-10-30 (Nils): a tap on the See it on you pill is not a tap on the picture */
      if (e.target && e.target.closest && e.target.closest('[data-int-tryon-open]')) return;
      open();
    }, true);

    /* Baymard: zoom that nobody can see is zoom that nobody uses. */
    var pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'int-zoom-pill';
    pill.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2B2B2B" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5M11 8v6M8 11h6"/></svg><span>Zoom</span>';
    pill.addEventListener('click', function (ev) { ev.stopPropagation(); open(); }, false);
    /* ⚑ DO NOT APPEND THIS TO .product__grid. Flickity treats any child of
       the grid as a CAROUSEL CELL: it swallowed the pill into
       .flickity-slider, stretched it to the cell width and pushed it
       11790px off to the right. It lives on the media wrapper instead,
       and its top is measured against the gallery each time the gallery
       is resized. */
    var mediaWrap = q('.product__media__wrapper');
    function placePill() {
      if (!mediaWrap || !grid) return;
      var g = grid.getBoundingClientRect(), w = mediaWrap.getBoundingClientRect();
      pill.style.top = Math.round(g.bottom - w.top - pill.offsetHeight - 8) + 'px';
    }
    function mountPill() {
      if (!mediaWrap) return;
      if (!mediaWrap.contains(pill)) mediaWrap.appendChild(pill);
      placePill();
    }
    mountPill();
    window.addEventListener('resize', function () { fitToViewport(); setTimeout(mountPill, 140); }, false);
    document.addEventListener('int:gallery:settled', mountPill, false);
    document.addEventListener('variant:change', function () { setTimeout(mountPill, 80); }, false);
    [300, 900, 1800, 3000].forEach(function (ms) { setTimeout(mountPill, ms); });
  }

  window.__intZoom = mount;

  /* Flickity has to exist before the pill can be measured against the
     gallery, and mount() re-places it on resize, on int:gallery:settled and
     on a short timer ladder anyway, so running at DOMContentLoaded is early
     enough without being too early to find .product__grid. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
